<template>
  <div class="section">
    <div class="flex justify-between items-center">
      <span class="font-body-2" :class="labelClass">{{ label }}</span>
      <el-tooltip
        v-if="tooltipText !== null"
        :content="tooltipText"
        placement="left"
        popper-class="applied-filters-tooltip"
      >
        <font-awesome-icon
          class="info-tooltip focus:outline-none"
          :icon="['far', 'info-circle']"
        />
      </el-tooltip>
    </div>
    <div
      class="min-h-9 rounded border border-accent-200 box-border p-[8px] pl-[10px] flex gap-[8px] cursor-pointer"
      @click="toggleExpansion()"
    >
      <div
        v-if="selectedTags.length === 0"
        class="empty-label font-body-3"
        :data-target="dataText"
      >
        {{ emptyLabel }}
      </div>
      <div v-else class="flex flex-wrap items-start align-middle gap-2">
        <Tag
          v-for="(selectedTag, index) in selectedTags"
          :key="index"
          :label="translateLabel ? $t(selectedTag.name) : selectedTag.name"
          variant="primary"
          @remove="emit('remove', selectedTag.id)"
        />
      </div>
    </div>
    <div v-show="isExpanded" class="relative border border-accent-200 rounded">
      <el-collapse-transition>
        <div
          class="absolute top-full left-0 z-10 mt-1 w-full bg-white border border-accent-200 rounded max-h-[300px] shadow-md p-2"
        >
          <div
            class="flex items-center gap-2 bg-white rounded px-2 h-9 overflow-hidden self-stretch"
          >
            <ClientOnly>
              <font-awesome-icon
                class="text-accent-500 w-4 h-4 shrink-0"
                :icon="['fas', 'magnifying-glass']"
              />
            </ClientOnly>
            <el-input
              ref="appliedFiltersInput"
              v-model="input"
              class="applied-filters-input flex-1"
              :placeholder="$t('navbar.monitor.search')"
              clearable
            />
          </div>
          <div class="list">
            <div
              v-for="listItem in filteredList"
              :key="listItem.id"
              class="list-row font-body-2"
              @click="emit('add', listItem)"
            >
              {{ translateLabel ? $t(listItem.name) : listItem.name }}
            </div>
          </div>
        </div>
      </el-collapse-transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Ref, ref, computed, nextTick } from "vue";
import Tag from "~/components/shared/RemovableTag.vue";
import ListItem from "~/types/ListItemType";
import MediaListDropdownItem from "~/types/MediaListDropdownItemType";

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  labelClass: {
    type: String,
    default: "",
  },
  emptyLabel: {
    type: String,
    required: true,
  },
  translateLabel: {
    type: Boolean,
    default: true,
  },
  list: {
    type: [Array<ListItem>, Array<MediaListDropdownItem>],
    required: true,
  },
  tooltipText: {
    type: String,
    default: null,
  },
  selectedTags: {
    type: [Array<ListItem>, Array<MediaListDropdownItem>],
    required: true,
  },
  dataText: {
    type: String,
    default: null,
  },
});

const isExpanded: Ref<boolean> = ref(false);
const input = ref("");
const appliedFiltersInput: Ref = ref(null);

const listWithoutSelections = computed(() => {
  const res: Array<ListItem> = [];
  props.list.forEach((item: any) => {
    const found = props.selectedTags.find((obj) => obj.id === item.id);
    if (!found) {
      res.push(item);
    }
  });
  return res;
});

const filteredList = computed(() => {
  if (input.value.length > 0) {
    return listWithoutSelections.value.filter((listItem) =>
      listItem.name.toLowerCase().includes(input.value.toLowerCase()),
    );
  }
  return listWithoutSelections.value;
});

const toggleExpansion = () => {
  isExpanded.value = !isExpanded.value;
  if (isExpanded.value) {
    // Allow the input time to be born, then focus
    nextTick(() => {
      appliedFiltersInput.value.focus();
    });
  }
};

const collapse = () => {
  isExpanded.value = false;
};

const emit = defineEmits(["add", "remove"]);
defineExpose({ collapse });
</script>

<style scoped>
.section {
  @apply flex flex-col w-full h-full flex gap-[1px];
}
.empty-label {
  @apply text-accent-400 h-5 flex items-center;
}
.info-tooltip {
  @apply w-4 h-4 rounded-full text-secondary-600;
}

.list {
  @apply max-h-[250px] overflow-y-auto;
}
.list-row {
  @apply p-2 h-9 hover:bg-accent-50 hover:font-bold cursor-pointer;
}
.applied-filters-input .el-input__wrapper::placeholder {
  @apply text-secondary-600 text-sm font-medium;
}
</style>

<style>
.applied-filters-input .el-input__wrapper {
  @apply placeholder-black px-3 py-2.5 shadow-none;
}
</style>
